<template>
  <div>
    <div class="container action-container-div">
      <h1 class="h1-action-plan">Insights</h1>
      <div class="about-plan-container mb-3">
        <p class="about-plan-p">FleetREDI, in tandem with our unique Fleet DNA 1 Hz operational database, provides actionable insights that can guide successful technology development and deployment for low- and zero-emissions commercial vehicles.</p>
        <p class="about-plan-p">The resources below provide ready-to-use insights as well as information on analysis methodologies and tools that can be applied to fill critical knowledge gaps.
        </p>
      </div>

      <div class="card featured-card card-zoom mb-4 rounded shadow w-100 text-white">
        <div class="row g-0 rounded">
          <div class="col-md-4 position-relative">
            <a href="https://docs.nlr.gov/docs/fy26osti/99419.pdf">
              <img src="@/assets/images/esb_and_plug.jpg" class="img-fluid rounded-start" alt="Image of a school bus">
            </a>
            <div class="card-img-overlay d-flex align-items-start p-2">
              <div class="d-inline-flex p-2 align-items-center justify-content-around bg-body-tertiary rounded shadow">
                <span class="badge bg-primary text-white shadow-sm me-2"><em>New</em></span>
                <p class="card-title text-black fs-5 m-0"><strong>Featured Insight</strong></p>
              </div>
            </div>
          </div>
          <div class="col-md-8">
            <div class="card-body p-4">
              <h5 class="card-title mb-3">
                <a href="https://docs.nlr.gov/docs/fy26osti/99419.pdf" class="text-white text-decoration-underline">
                  <strong>Cold Weather Impacts on Electric School Bus Performance in Aurora, Colorado</strong>
                </a>
              </h5>
              <p class="text-white"><em>2026&mdash;<strong>Analysis Brief</strong></em></p>
              <p><strong>Key Takeaway: </strong> Electric school buses operate 2–5 times more efficiently than conventional buses on average. Cold weather can double electric school bus energy demands, but strategies such as thermal pre-conditioning significantly reduce this effect. Understanding these impacts will help fleets plan charging, dispatching, and purchase decisions.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://docs.nlr.gov/docs/fy26osti/94381.pdf"><img src="@/assets/images/white_tractor_trailer.jpg" alt="Image of a green tractor trailer" class="img-fluid rounded"></a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3"><strong><a href="https://docs.nlr.gov/docs/fy26osti/94381.pdf" class="text-black text-decoration-underline">Analysis of Truck Platooning on Rural Highways</a></strong></h5>
              <p><em>2026&mdash;<strong>Analysis Brief</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong> On rural Ohio highways, platooning technology operated for 40% of observed driving distances, which could result in savings of about $1,200 (or 319 gallons of diesel) per year. It shows potential to further increase cost savings through reduced gap distances and increased system usage. The demonstration also showcased platooning’s potential to enhance freight efficiency and safety.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://www.nlr.gov/docs/fy25osti/91370.pdf">
              <img src="@/assets/images/purple-buses.jpg" alt="Image of purple buses" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://www.nlr.gov/docs/fy25osti/91370.pdf" class="text-black text-decoration-underline">Electrification Analysis: All Aboard America!</a></strong>
              </h5>
              <p><em>2024&mdash;<strong>Analysis Brief</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>An interregional express bus service can use a currently available battery capacity option (676 kWh) at a charge rate of 125 kW to immediately electrify part of its fleet for routes up to 200 miles in moderate climates, but the range of current market options limits full electrification.</p>
              <p><small><em><strong>Related Resource:</strong> Learn more about the <a href="https://livewire.energy.gov/ds/fleetredi/fleetredi-interstate-coach-bus">FleetREDI Insight: Intrastate Coach Bus Dataset</a> on the Livewire website.</em></small></p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://docs.nlr.gov/docs/fy26osti/99452.pdf">
              <img src="@/assets/images/transit-bus-laos-nanayakkara.jpg" alt="Image of a passenger bus" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://docs.nlr.gov/docs/fy26osti/99452.pdf" class="text-black text-decoration-underline">Scalable GPS Data Logging To Support Advanced Fleet Analysis</a></strong>
              </h5>
              <p><em>2025&mdash;<strong>Analysis Brief</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>NLR developed and demonstrated low-cost, open-source Arduino data loggers with a 3D-printed case that are compatible with global navigational systems and built with components available ubiquitously worldwide, enabling cost-effective collection and analysis of fleet operational data. Validated on an overseas transit bus fleet, NLR analysis showed that, with sufficient charging opportunities, 90% of observed duty cycles could be accomplished by electric buses with no modifications to operations.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://www.nlr.gov/docs/fy25osti/91369.pdf">
              <img src="@/assets/images/pepsi-truck-fleet.jpeg" alt="Image of a tractor trailer" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://www.nlr.gov/docs/fy25osti/91369.pdf" class="text-black text-decoration-underline">Estimating Electrification Potential for Class 8 Regional-Haul Trucks</a></strong>
              </h5>
              <p><em>2024&mdash;<strong>Analysis Brief</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>Simulated Tesla trucks, with an average efficiency of 1.8 kWh/mi and daily driving distances ranging from an average of 445 to 1,100 miles, face challenges on longer routes and struggle to achieve full operational coverage with the current battery and charging configurations. However, where ubiquitous charging exists, 100% EV coverage is possible for the given drive cycles.</p>
              <p><small><em><strong>Related Resource:</strong> Learn more about the data used for estimating Class 8 electrification potential on the <a href="https://results-2023.runonless.com/">Run On Less website</a>.</em></small></p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://www.nlr.gov/docs/fy24osti/91423.pdf">
              <img src="@/assets/images/EPRI.png" alt="Image of a green tractor trailer" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://www.nlr.gov/docs/fy24osti/91423.pdf" class="text-black text-decoration-underline">FleetREDI Electrification Analysis: Container Ports’ Cargo Handling Equipment</a></strong>
              </h5>
              <p><em>2024&mdash;<strong>Analysis Brief</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>As seaports look to electrify operations in a bid to reduce airborne emissions, the National Laboratory of the Rockies (NLR) developed a model—one of the first of its kind—to understand seaport energy demand at each EV equipment level. The model is easily scalable to container demand and EV adoption rate projections. The analysis estimated that a fully electrified maritime port would require 14.6–20.6 kWh per container imported.</p>
              <p><small><em><strong>Related Resource:</strong> <a href="https://data.nlr.gov/submissions/281">Modeled Electricity Demand Profiles for Electric Port Cargo Handling Equipment in the United States Dataset</a></em></small></p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://www.nlr.gov/docs/fy24osti/90704.pdf">
              <img src="@/assets/images/pdf_img.png" alt="Image of a green tractor trailer" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://www.nlr.gov/docs/fy24osti/90704.pdf" class="text-black text-decoration-underline">FleetREDI Electrification Analysis: Manhattan Beer</a></strong>
              </h5>
              <p><em>2024&mdash;<strong>Analysis Brief</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>Class-8 beverage distribution trucks operating in Manhattan show substantial electrification potential due to daily driving distances below 50 miles and low average speeds of 22mph or less. Their duty cycle needs can often be met by even modestly sized batteries and charging infrastructure. Vulnerable communities near their routes would benefit from fleet electrification.</p>
              <p><small><em><strong>Related Resource:</strong> Learn more about the <a href="https://livewire.energy.gov/ds/fleetredi/fleetredi-beverage-delivery-nyc">FleetREDI Insight: Beverage Delivery in New York City Dataset</a> on the Livewire website.</em></small></p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://www.nlr.gov/docs/fy24osti/88477.pdf">
              <img src="@/assets/images/clean-delivery.jpg" alt="Image of a truck making a delivery" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://www.nlr.gov/docs/fy24osti/88477.pdf" class="text-black text-decoration-underline">Delivering Clean Air in Denver: Propane Trucks and Infrastructure in Mail Delivery Application</a></strong>
              </h5>
              <p><em>2024&mdash;<strong>Technical Report</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>Class-6 propane trucks can attain nearly the same performance as diesel fleets while lowering carbon dioxide tailpipe emissions and nearly eliminating other tailpipe criteria pollutant emissions. While propane trucks represent a higher upfront investment than comparable diesel trucks, lower operating costs and potential rebates both serve to lower the total cost of ownership.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://www.sciencedirect.com/science/article/pii/S0306261922017408?via%3Dihub">
              <img src="@/assets/images/shuttle-transport.jpg" alt="Image of an airport shuttle bus" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://www.sciencedirect.com/science/article/pii/S0306261922017408?via%3Dihub" class="text-black text-decoration-underline">Data-driven simulation-based planning for electric airport shuttle systems: A real-world case study</a></strong>
              </h5>
              <p><em>2023&mdash;<strong>Journal Article</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>A simulation-based model created by NLR researchers can effectively optimize the planning of electric airport shuttle systems in both partial and full fleet electrification scenarios. This model was successfully implemented in a study for Dallas-Fort Worth International airport, where it identified strategies for a proposed e-bus fleet that minimized total infrastructure costs, capital investments, customer wait times, and tailpipe emissions.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://saemobilus.sae.org/content/2023-01-0708">
              <img src="@/assets/images/hevii-tool-heavy-duty.jpg" alt="Image of a truck plugged into charge" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://saemobilus.sae.org/content/2023-01-0708" class="text-black text-decoration-underline">Development of a Heavy-Duty Electric Vehicle Integration and Implementation (HEVII) Tool: SAE Technical Paper No. 2023-01-0708</a></strong>
              </h5>
              <p><em>2023&mdash;<strong>Journal Article</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>NLR’s Heavy-Duty Electric Vehicle Integration and Implementation (HEVII) tool can assess the suitability of each Class 6-8 vehicle within a fleet for electrification, in addition to identifying optimal charger placement and providing cost analyses. HEVII can enable manufacturers and fleet operators to cost-effectively transition fleets to heavy-duty electric vehicles.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://www.nlr.gov/docs/fy24osti/89761.pdf">
              <img src="@/assets/images/zanzeff.jpg" alt="Image of trucks" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://www.nlr.gov/docs/fy24osti/89761.pdf" class="text-black text-decoration-underline">The Port of Los Angeles Zero- and Near-Zero-Emission Freight Facilities "Shore to Store" Project</a></strong>
              </h5>
              <p><em>2023&mdash;<strong>Project Final Report</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>Using a combination of hydrogen fuel cell trucks and electric cargo handling equipment, freight facilities are capable of transitioning to net-zero emissions operations using today’s technology. The Zero- and Near-Zero-Emission Freight Facilities (ZANZEFF) Shore to Store project demonstrated this model via goods movement from ports to storefronts across Southern California.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://journals.sagepub.com/doi/10.1177/03611981221095744">
              <img src="@/assets/images/aspires.jpg" alt="Image of airport shuttle buses" class="img-fluid rounded" style="height: 60%!important;">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://journals.sagepub.com/doi/10.1177/03611981221095744" class="text-black text-decoration-underline">ASPIRES: Airport Shuttle Planning and Improved Routing Event-driven Simulation</a></strong>
              </h5>
              <p><em>2022&mdash;<strong>Journal Article</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>This article describes the data-driven, computationally efficient Airport Shuttle Planning and Improved Routing Event-driven Simulation (ASPIRES) package, which can evaluate current, potential, and future airport shuttle operations. ASPIRES simulates travel times, dwell times, and passenger arrivals using empirical distributions derived mainly from real data, capturing stochasticity. ASPIRES is well suited for fleet systems analysis.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://www.sciencedirect.com/science/article/pii/S1361920922000815?via%3Dihub">
              <img src="@/assets/images/developing-heavy-duty-db.jpg" alt="Image of tractor trailer on the road surrounded by wind power" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://www.sciencedirect.com/science/article/pii/S1361920922000815?via%3Dihub" class="text-black text-decoration-underline">Developing a Heavy-Duty Vehicle Activity Database to Estimate Start and Idle Emissions: Article No. 103251</a></strong>
              </h5>
              <p><em>2022&mdash;<strong>Journal Article</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>This article provides the most comprehensive, publicly available analysis of heavy-duty vehicle start and idling activity within the United States. The results are useful for improving emissions estimates, and capture uncertainty due to variation across fleets and vocations. For example, we find that refuse trucks start less than 6 times per vehicle per weekday on average while single-unit short-haul parcel delivery trucks start 125 times per day on average.</p>
              <p class="mb-0"><small><em><strong>Related Resources:</strong></em></small></p>
                <ul>
                  <li><em><small><a href="https://docs.nlr.gov/docs/fy21osti/79509.pdf">Heavy-Duty Vehicle Activity Updates for MOVES Using NREL Fleet DNA and CE-CERT Data (Techincal Report)</a></small></em></li>
                  <li><em><small><a href="https://data.nlr.gov/submissions/168">Heavy-Duty Vehicle Activity for EPA MOVES (Dataset)</a></small></em></li>
                </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://www.nlr.gov/docs/fy23osti/83400.pdf">
              <img src="@/assets/images/ny-nj-port.jpg" alt="Image of the NY and New Jersey Port" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://www.nlr.gov/docs/fy23osti/83400.pdf" class="text-black text-decoration-underline">Port of New York and New Jersey Drayage Electrification Analysis</a></strong>
              </h5>
              <p><em>2022&mdash;<strong>Technical Report</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>NLR researchers conducted detailed data collection and evaluation of three Class-8 drayage fleets at the Port of New York and New Jersey and found that electrification of a portion of the fleet operations is possible using today’s electric truck technology. Researchers also identified performance requirements for suitable battery-electric trucks and corresponding charging infrastructure.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card insight-card card-zoom mb-4 w-100 h-75 bg-light shadow rounded">
        <div class="row g-0 rounded">
          <div class="col-md-3 p-2">
            <a href="https://journals.sagepub.com/doi/abs/10.1177/14680874211029905">
              <img src="@/assets/images/engine-speed-torque.png" alt="Image of a green truck" class="img-fluid rounded">
            </a>
          </div>
          <div class="col-md-8">
            <div class="card-body p-2">
              <h5 class="card-title mb-3">
                <strong><a href="https://journals.sagepub.com/doi/abs/10.1177/14680874211029905" class="text-black text-decoration-underline">Development of In-Use Engine Speed/Torque Heat Maps Across Multiple Heavy-Duty Commercial Vehicle Vocations</a></strong>
              </h5>
              <p><em>2021&mdash;<strong>Journal Article</strong></em></p>
              <p class="mt-3"><strong>Key Takeaway: </strong>This article describes the development of in-use engine heat maps showing engine operating points, generated from extensive datalogging of in-service trucks. The engine speed/torque heat maps and efficiency points show significant variation across nine different truck vocations and illustrate that many vehicle applications spend significant time operating in regions outside the “sweet spot” for peak engine efficiency.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <CitationNote />
  </div>
</template>

<script setup>
import CitationNote from '@/components/CitationNote.vue';
</script>
